/**
 * After a Quick Bill is saved, add the items typed on it to the catalogue so
 * they are suggested — and sellable in normal billing — next time. Owner's
 * decision (2026-09-29): "Also add to the catalogue".
 *
 * Only AFTER the save, so an abandoned bill leaves nothing behind. The bill
 * itself already carries these lines as manual items (billing/manualItem.ts);
 * the catalogue entries are for the future, not for this bill.
 *
 * Added as non-stock products (trackInventory false, stock 0): a typed line
 * says nothing about how many the shop holds, and a stock-tracked product at 0
 * would be refused the next time someone bills it. An HSN starting 99 is a
 * SAC — a service — and is marked as one.
 */
import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { createProduct } from '@/inventory/InventoryCrudService';
import { catalogueCandidates, type QuickBillRow, type RateMode } from './quickBillForm';

export async function addTypedItemsToCatalogue(
  storeId: string,
  rows: readonly QuickBillRow[],
  mode: RateMode,
): Promise<number> {
  const names = rows.filter((r) => !r.catalogue && r.name.trim()).map((r) => r.name.trim());
  if (names.length === 0) return 0;

  // The catalogue names these could collide with. `like` without wildcards is
  // an equality test that ignores case, which is what "already have it" means.
  const existing: string[] = [];
  for (const n of names) {
    const hits = await database
      .get<Product>('products')
      .query(Q.where('store_id', storeId), Q.where('is_deleted', false), Q.where('name', Q.like(Q.sanitizeLikeString(n))))
      .fetch();
    existing.push(...hits.map((h) => h.name));
  }

  const toAdd = catalogueCandidates(rows, mode, existing);
  for (const c of toAdd) {
    await createProduct({
      storeId,
      name: c.name,
      hsnCode: c.hsn,
      // As bulk import does: with no MRP given, the selling price is the MRP.
      // 0 would print "MRP ₹0" wherever a receipt shows MRP.
      mrp: c.sellingPrice,
      sellingPrice: c.sellingPrice,
      purchasePrice: 0,
      currentStock: 0,
      trackInventory: false,
      isService: (c.hsn ?? '').startsWith('99'),
      unit: c.unit,
      gstRate: c.gstRate,
      cessRate: c.cessRate,
      isActive: true,
    });
  }
  return toAdd.length;
}
